import dataset from '../../../services/demo_data/dataset.json';

export interface Mode {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
}

export interface Portfolio {
  id: string;
  name: string;
  risk_profile?: string;
  modes: Mode[];
  hasLiveEnabled?: boolean;
  tenant?: TenantMeta;
}

export interface TenantMeta {
  id?: string;
  name?: string;
  type?: string;
  [key: string]: unknown;
}

export interface Organization {
  id: string;
  name: string;
  status: string;
  memberCount?: number;
  created_at?: string;
  updated_at?: string;
  roles?: string[];
  isPinned?: boolean;
  isRecent?: boolean;
  portfolios?: Portfolio[];
}

const datasetOrgs = (dataset as any)?.scope?.organizations ?? [];

const mapMode = (mode: any): Mode => ({
  id: mode?.id ?? 'mode-paper',
  name: mode?.name ?? 'Paper',
  description: mode?.description,
  enabled: Boolean(mode?.enabled ?? mode?.status !== 'disabled'),
});

const extractTenantMeta = (tenant: any): TenantMeta | undefined => {
  if (!tenant || typeof tenant !== 'object') {
    return undefined;
  }
  const meta: TenantMeta = {};
  for (const key of Object.keys(tenant)) {
    if (key === 'portfolios') {
      continue;
    }
    meta[key as keyof TenantMeta] = tenant[key];
  }
  return Object.keys(meta).length ? meta : undefined;
};

const mapPortfolio = (portfolio: any): Portfolio => {
  const modes: Mode[] = Array.isArray(portfolio?.modes)
    ? portfolio.modes.map(mapMode)
    : [mapMode({ id: 'mode-paper', name: 'Paper', enabled: true })];
  const hasLiveEnabled =
    typeof portfolio?.hasLiveEnabled === 'boolean'
      ? portfolio.hasLiveEnabled
      : modes.some(
          (mode: Mode) =>
            mode.name.toLowerCase() === 'live' ||
            mode.id.toLowerCase().includes('live')
        ) && modes.some((mode: Mode) => mode.enabled);

  return {
    id: portfolio?.id ?? 'portfolio-demo',
    name: portfolio?.name ?? 'Demo Portfolio',
    risk_profile: portfolio?.risk_profile,
    modes,
    hasLiveEnabled,
    tenant: extractTenantMeta(portfolio?.tenant),
  };
};

const mapOrganization = (org: any): Organization => {
  const portfolios: Portfolio[] = Array.isArray(org?.portfolios)
    ? org.portfolios.map(mapPortfolio)
    : [mapPortfolio(undefined)];

  return {
    id: org?.id ?? 'org-demo',
    name: org?.name ?? 'Demo Organization',
    status: org?.status ?? 'active',
    memberCount: org?.memberCount,
    created_at: org?.created_at,
    updated_at: org?.updated_at,
    roles: Array.isArray(org?.roles) ? org.roles.map(String) : [],
    isPinned: Boolean(org?.isPinned),
    isRecent: Boolean(org?.isRecent),
    portfolios,
  };
};

const hierarchicalOrganizations: Organization[] = datasetOrgs.length
  ? datasetOrgs.map(mapOrganization)
  : [mapOrganization(undefined)];

export { hierarchicalOrganizations };
export const defaultOrganization = hierarchicalOrganizations[0];
export const defaultPortfolio = defaultOrganization?.portfolios?.[0] ?? mapPortfolio(undefined);
export const defaultMode = defaultPortfolio?.modes?.[0] ?? mapMode(undefined as any);
